import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { showDate } from '../../shared/tree'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MONTH_NAMES, monthGrid, partialDate, readDate, yearPage } from '@/lib/dates'
import { cn } from '@/lib/utils'

type View = 'years' | 'months' | 'days'

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

// A date as rough as the family knows it. Picking a year sets the year and opens its months; picking a month
// narrows it and opens its days; picking a day finishes. So someone can stop at whichever they know.
export function DatePicker({
  id,
  value,
  onChange,
  placeholder = 'Not known',
  className,
  'aria-describedby': describedBy,
}: {
  id?: string
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  className?: string
  'aria-describedby'?: string
}) {
  const [open, setOpen] = useState(false)
  const panel = useId()
  const field = useRef<HTMLButtonElement>(null)
  const opened = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (open) opened.current?.scrollIntoView({ block: 'nearest' })
  }, [open])
  const close = () => {
    setOpen(false)
    field.current?.focus()
  }
  return (
    <div className="grid gap-2">
      <button
        ref={field}
        id={id}
        type="button"
        aria-describedby={describedBy}
        aria-expanded={open}
        aria-controls={panel}
        onClick={() => setOpen(!open)}
        className={cn(
          'flex h-11 w-full min-w-0 items-center gap-2.5 rounded-xl border border-input bg-background px-4 text-left text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm',
          'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
          className,
        )}
      >
        <CalendarDays className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        {value ? showDate(value) : <span className="text-muted-foreground">{placeholder}</span>}
      </button>
      {/* The calendar opens in place under the field rather than floating over the page, so it's never cut off on
          a short phone screen or inside a dialog. Its arrow keys stay its own rather than stepping through photos.
          Remounted on each opening, so it starts from the date as it is now. */}
      {open && (
        <div
          id={panel}
          data-slot="date-picker-panel"
          ref={opened}
          className="w-full max-w-80 animate-in rounded-3xl bg-popover p-3 text-popover-foreground shadow-lift duration-200 fade-in-0"
          onKeyDown={(e) => {
            if (e.key === 'Escape') close()
            if (['Escape', 'ArrowLeft', 'ArrowRight'].includes(e.key)) e.stopPropagation()
          }}
        >
          <Calendar value={value} onChange={onChange} onClose={close} />
        </div>
      )}
    </div>
  )
}

function Calendar({
  value,
  onChange,
  onClose,
}: {
  value: string | null
  onChange: (value: string | null) => void
  onClose: () => void
}) {
  const today = new Date()
  const now = { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() }
  const picked = readDate(value)
  const [view, setView] = useState<View>(picked.month ? 'days' : picked.year ? 'months' : 'years')
  const [year, setYear] = useState(picked.year ?? now.year)
  const [month, setMonth] = useState(picked.month ?? now.month)
  // Photos and lives are in the past, so nothing after today can be picked.
  const future = (y: number, m?: number, d?: number) =>
    y > now.year ||
    (y === now.year && m !== undefined && (m > now.month || (m === now.month && d !== undefined && d > now.day)))

  const years = yearPage(year, now.year)
  const header =
    view === 'days'
      ? {
          label: `${MONTH_NAMES[month - 1]} ${year}`,
          up: 'Choose a month',
          prev: 'Previous month',
          next: 'Next month',
          back: () => (month === 1 ? (setYear(year - 1), setMonth(12)) : setMonth(month - 1)),
          forward: () => (month === 12 ? (setYear(year + 1), setMonth(1)) : setMonth(month + 1)),
          last: future(month === 12 ? year + 1 : year, month === 12 ? 1 : month + 1),
          onUp: () => setView('months'),
        }
      : view === 'months'
        ? {
            label: String(year),
            up: 'Choose a year',
            prev: 'Previous year',
            next: 'Next year',
            back: () => setYear(year - 1),
            forward: () => setYear(year + 1),
            last: year >= now.year,
            onUp: () => setView('years'),
          }
        : {
            label: `${years[0]} – ${years.at(-1)}`,
            up: null,
            prev: 'Earlier years',
            next: 'Later years',
            back: () => setYear(year - 12),
            forward: () => setYear(Math.min(year + 12, now.year)),
            last: years.at(-1)! >= now.year,
            onUp: undefined,
          }

  const cell =
    'grid place-items-center rounded-full text-sm outline-none transition-colors hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-35 aria-pressed:bg-brand aria-pressed:font-semibold aria-pressed:text-brand-foreground'

  // Every button here says type="button": the picker sits inside forms, and a plain button there submits the form,
  // so stepping back a year would save and close it.
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-10"
          aria-label={header.prev}
          onClick={header.back}
        >
          <ChevronLeft aria-hidden />
        </Button>
        {header.up ? (
          <Button
            type="button"
            variant="ghost"
            className="h-10 px-3 font-semibold"
            aria-label={`${header.label}. ${header.up}`}
            onClick={header.onUp}
          >
            {header.label}
          </Button>
        ) : (
          <p className="text-sm font-semibold" aria-live="polite">
            {header.label}
          </p>
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-10"
          aria-label={header.next}
          disabled={header.last}
          onClick={header.forward}
        >
          <ChevronRight aria-hidden />
        </Button>
      </div>

      {view === 'years' && (
        <div className="grid grid-cols-3 gap-1.5">
          {years.map((y) => (
            <button
              key={y}
              type="button"
              aria-pressed={picked.year === y}
              disabled={future(y)}
              className={cn(cell, 'h-12', y === now.year && 'font-semibold')}
              onClick={() => {
                onChange(partialDate(y))
                setYear(y)
                setView('months')
              }}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {view === 'months' && (
        <div className="grid grid-cols-3 gap-1.5">
          {MONTH_NAMES.map((name, i) => (
            <button
              key={name}
              type="button"
              aria-label={`${name} ${year}`}
              aria-pressed={picked.year === year && picked.month === i + 1}
              disabled={future(year, i + 1)}
              className={cn(cell, 'h-12')}
              onClick={() => {
                onChange(partialDate(year, i + 1))
                setMonth(i + 1)
                setView('days')
              }}
            >
              {name.slice(0, 3)}
            </button>
          ))}
        </div>
      )}

      {view === 'days' && (
        <div className="grid grid-cols-7 gap-0.5" role="group" aria-label={`${MONTH_NAMES[month - 1]} ${year}`}>
          {WEEKDAYS.map((d, i) => (
            <span key={i} aria-hidden="true" className="grid h-8 place-items-center text-xs text-muted-foreground">
              {d}
            </span>
          ))}
          {monthGrid(year, month).map((d, i) =>
            d === null ? (
              <span key={`blank-${i}`} />
            ) : (
              <button
                key={d}
                type="button"
                aria-label={showDate(partialDate(year, month, d))}
                aria-pressed={picked.year === year && picked.month === month && picked.day === d}
                disabled={future(year, month, d)}
                className={cn(
                  cell,
                  'aspect-square',
                  year === now.year && month === now.month && d === now.day && 'font-semibold underline',
                )}
                onClick={() => {
                  onChange(partialDate(year, month, d))
                  onClose()
                }}
              >
                {d}
              </button>
            ),
          )}
        </div>
      )}

      {view === 'years' && (
        <TypeYear
          thisYear={now.year}
          onYear={(y) => {
            onChange(partialDate(y))
            setYear(y)
            setView('months')
          }}
        />
      )}
      <p className="px-1 text-sub text-muted-foreground">
        {view === 'years'
          ? 'Pick the year. You can stop there if that’s all anyone knows.'
          : view === 'months'
            ? 'Pick the month if you know it.'
            : 'Pick the day if you know it.'}
      </p>
      <div className="flex items-center justify-between gap-2 border-t pt-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!value}
          onClick={() => {
            onChange(null)
            setView('years')
          }}
        >
          Clear
        </Button>
        <Button type="button" size="sm" onClick={onClose}>
          Done
        </Button>
      </div>
    </div>
  )
}

// Long ago is many pages back, so a year can be typed too.
function TypeYear({ thisYear, onYear }: { thisYear: number; onYear: (year: number) => void }) {
  const [text, setText] = useState('')
  const year = Number(text)
  const ok = /^\d{4}$/.test(text) && year <= thisYear
  return (
    <div className="flex gap-2">
      <Input
        aria-label="Type a year"
        inputMode="numeric"
        maxLength={4}
        placeholder="Or type a year"
        value={text}
        onChange={(e) => setText(e.target.value.replace(/\D/g, ''))}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          e.preventDefault()
          if (ok) onYear(year)
        }}
      />
      <Button type="button" variant="outline" disabled={!ok} onClick={() => onYear(year)}>
        Go
      </Button>
    </div>
  )
}
